import { useImageAnnotatorContext } from "@msuite/image-annotator";
import type { DimensionAnnotation, Point } from "@msuite/utils/types";
import { useTranslation } from "../../localization";

/** Props Interface */
interface AnnotationChainHandleProps {
	annotation: DimensionAnnotation;
	/** Where the handle is drawn — beyond the measured point, clear of the drag handle. */
	cx: number;
	cy: number;
	/** The measured point a chained dimension starts from. */
	origin: Point;
}

export function AnnotationChainHandle({ annotation, cx, cy, origin }: AnnotationChainHandleProps) {
	/** Context */
	const context = useImageAnnotatorContext();
	const combinedScale = context.baseScale * context.scale;

	/** Hooks */
	const { t } = useTranslation();

	/** Computed */
	const radius = 8 / combinedScale;
	const arm = 4 / combinedScale;

	/** Functions */
	function handleOnClick(e: React.MouseEvent<SVGCircleElement>) {
		e.stopPropagation();
		context.controller.handleOnStartDimensionChain(origin, annotation);
	}

	/** Render */
	return (
		<g>
			<title>{t("image_annotator.continue_dimension")}</title>
			<circle
				cx={cx}
				cy={cy}
				r={radius}
				fill="white"
				stroke="black"
				strokeWidth={1 / combinedScale}
				pointerEvents="none"
			/>
			<line
				x1={cx - arm}
				y1={cy}
				x2={cx + arm}
				y2={cy}
				stroke="black"
				strokeWidth={1.5 / combinedScale}
				pointerEvents="none"
			/>
			<line
				x1={cx}
				y1={cy - arm}
				x2={cx}
				y2={cy + arm}
				stroke="black"
				strokeWidth={1.5 / combinedScale}
				pointerEvents="none"
			/>
			<circle
				cx={cx}
				cy={cy}
				r={20 / combinedScale}
				fill="transparent"
				onClick={handleOnClick}
				style={{ cursor: "pointer", touchAction: "none" }}
			/>
		</g>
	);
}
